/**
 * @file useEditableList.js
 * @description 목록형 데이터를 초안(draft) 상태로 편집하는 공용 훅입니다.
 * @used-in pages/admin/HistoryAdminPanel.jsx, pages/admin/FaqAdminPanel.jsx, pages/admin/ContentAdminPanel.jsx
 *
 * [비즈니스] 연혁과 FAQ는 "전체 목록을 통째로 교체"하는 동일한 저장 방식을 씁니다.
 *            편집 중인 내용은 저장 버튼을 누르기 전까지 서버에 반영되지 않아야 하므로
 *            화면 안에서만 유지되는 초안 상태를 별도로 관리합니다.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

// [기술] 행을 구분하는 클라이언트 전용 식별자입니다.
//        값(연도+내용 등)을 키로 쓰면 내용이 완전히 같은 행이 둘 있을 때 서로 구분되지 않아
//        하나를 지울 때 둘 다 지워집니다. 저장 시에는 이 필드를 제거합니다.
let uidCounter = 0;
function nextUid() {
  uidCounter += 1;
  return `row-${uidCounter}`;
}

// [기능] list 에서 fromIndex 항목을 빼 toIndex 자리에 넣은 새 배열을 반환합니다.
//        정수가 아니거나 범위 밖이거나 같은 인덱스면 원본 배열을 반환합니다.
// [기술] 원본 참조를 반환해 무의미한 재렌더와 변경 감지를 막습니다.
function moveItem(list, fromIndex, toIndex) {
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return list;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return list;
  if (fromIndex === toIndex) return list;
  const next = list.slice();
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

// [기능] 목록 편집 상태와 조작 함수를 제공합니다.
// @param {Function} makeEmptyRow 새 행의 초기값을 만드는 팩토리
// @param {object}   options      addTo: 'top' | 'bottom' — 새 행을 목록의 어느 쪽에 넣을지
// @returns 편집 상태(rows, isDirty)와 조작 함수 모음
export function useEditableList(makeEmptyRow, { addTo = 'bottom' } = {}) {
  const [rows, setRows] = useState([]);
  // [기술] 서버에서 받은 시점의 스냅샷입니다. 변경 여부(isDirty) 판단 기준이 됩니다.
  const snapshotRef = useRef('[]');

  // [기능] 서버에서 받은 목록으로 초안을 초기화합니다.
  const setRowsFromServer = useCallback((serverItems) => {
    const nextRows = (serverItems || []).map((item) => ({
      ...item,
      _uid: nextUid(),
      _isNew: false,
      _deleted: false,
    }));
    snapshotRef.current = JSON.stringify(serverItems || []);
    setRows(nextRows);
  }, []);

  // [기능] 특정 행의 한 필드를 수정합니다.
  const updateRow = useCallback((uid, field, value) => {
    setRows((prev) => prev.map((row) => (row._uid === uid ? { ...row, [field]: value } : row)));
  }, []);

  // [기능] 기존 행의 삭제 표시를 켜고 끕니다.
  // [비즈니스] 즉시 지우지 않고 표시만 해 두면, 저장 전에 실수를 되돌릴 수 있습니다.
  const toggleDelete = useCallback((uid) => {
    setRows((prev) => prev.map((row) => (row._uid === uid ? { ...row, _deleted: !row._deleted } : row)));
  }, []);

  // [기능] 빈 행을 목록에 추가합니다. addTo 옵션에 따라 맨 위 또는 맨 아래에 놓입니다.
  // [비즈니스] 목록 순서가 그대로 공개 페이지 노출 순서가 되므로,
  //            최신 항목을 먼저 보여야 하는 목록은 새 행을 맨 위에 넣습니다.
  const addRow = useCallback(() => {
    const newRow = { ...makeEmptyRow(), _uid: nextUid(), _isNew: true, _deleted: false };
    setRows((prev) => (addTo === 'top' ? [newRow, ...prev] : [...prev, newRow]));
  }, [makeEmptyRow, addTo]);

  // [기능] 아직 저장되지 않은 새 행을 목록에서 제거합니다.
  const removeRow = useCallback((uid) => {
    setRows((prev) => prev.filter((row) => row._uid !== uid));
  }, []);

  // [기능] 행을 위(-1) 또는 아래(+1)로 옮기고 목록 경계를 넘으면 그대로 둡니다.
  // [비즈니스] 목록 순서가 공개 노출 순서이며 삭제 표시 행도 자리를 지킨 채 함께 이동합니다.
  const moveRow = useCallback((uid, offset) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row._uid === uid);
      if (index < 0) return prev;
      return moveItem(prev, index, index + offset);
    });
  }, []);

  // [기능] uid 로 찾은 행을 이동 후 toIndex 자리로 옮기며 유효하지 않은 이동은 무시합니다.
  // [비즈니스] 여러 칸을 건너뛰어도 삭제 표시와 새 행 표시는 함께 이동합니다.
  const moveRowTo = useCallback((uid, toIndex) => {
    setRows((prev) => moveItem(prev, prev.findIndex((row) => row._uid === uid), toIndex));
  }, []);

  // [기능] 편집 내용을 서버 스냅샷 상태로 되돌립니다.
  const resetDraft = useCallback(() => {
    setRowsFromServer(JSON.parse(snapshotRef.current));
  }, [setRowsFromServer]);

  // [기능] 저장용 배열을 만듭니다. 삭제 표시된 행과 mapRow가 null을 반환한 행은 제외합니다.
  // @param {Function} mapRow 행을 저장 형식으로 변환하는 함수. 빈 행이면 null을 반환하면 됩니다.
  const buildPayload = useCallback(
    (mapRow) => rows.filter((row) => !row._deleted).map(mapRow).filter((item) => item !== null),
    [rows]
  );

  // [기능] 서버 상태 대비 변경 사항이 있는지 여부입니다. 저장 버튼 활성화에 사용합니다.
  const isDirty = useMemo(() => {
    if (rows.some((row) => row._deleted || row._isNew)) return true;
    // [기술] 클라이언트 전용 필드를 제외하고 비교해 서버 스냅샷과 형태를 맞춥니다.
    const current = rows.map(({ _uid, _isNew, _deleted, ...rest }) => rest);
    return JSON.stringify(current) !== snapshotRef.current;
  }, [rows]);

  return { rows, setRowsFromServer, updateRow, toggleDelete, addRow, removeRow, moveRow, moveRowTo, resetDraft, buildPayload, isDirty };
}
